import { PlatformEarningsBreakdown } from '@/components/reports/PlatformEarningsBreakdown'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { FloristStatusBadge } from '@/components/organizations/FloristStatusBadge'
import { Badge } from '@/components/ui/badge'
import type { DirectionalSortConfig } from '@/components/ui/sortable-column-header'
import dayjs from '@/lib/dayjs'
import {
  FLORIST_PERFORMANCE_MOMENTUM_METRIC,
  FLORIST_PERFORMANCE_VIEW,
} from '@/lib/services/reports/reports.schema'
import type {
  FloristPerformanceReportMomentumMetric,
  FloristPerformanceReportOrderCounts,
  FloristPerformanceReportOrganization,
  FloristPerformanceReportRow,
  FloristPerformanceReportRowMetrics,
  FloristPerformanceReportSortField,
  FloristPerformanceReportSourceAmounts as SourceAmounts,
  FloristPerformanceReportSummary,
  FloristPerformanceReportView,
} from '@/lib/services/reports/reports.types'
import { cn, formatCurrency, formatNumber } from '@/lib/utils'
import type { ColumnDef } from '@tanstack/react-table'

type Column = ColumnDef<FloristPerformanceReportRow>
type MomentumMetric = FloristPerformanceReportMomentumMetric
/** The views whose block is money by source. */
type MoneyView = Extract<FloristPerformanceReportView, 'customer_sales' | 'platform_revenue'>
type MomentumWindow = 'current_7' | 'prior_7' | 'current_30' | 'prior_30'

const numberWithDecimals = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
})

type ColumnOptions = { view: FloristPerformanceReportView; momentumMetric: MomentumMetric }

export function getFloristPerformanceColumns({ view, momentumMetric }: ColumnOptions): Column[] {
  const columns: Column[] = [
    {
      id: 'florist',
      accessorFn: (row) => row.organization.name,
      header: 'Florist',
      meta: {
        kind: 'primary',
        minWidth: '14rem',
        sortable: sortDirections('florist'),
      },
    },
    {
      id: 'status',
      accessorFn: (row) => row.organization.status,
      header: 'Status',
      cell: ({ row }) => <FloristStatusBadge status={row.original.organization.status} />,
      meta: {
        kind: 'badge',
        align: 'center',
        link: false,
        sortable: sortDirections('status'),
      },
    },
    {
      id: 'go_live_at',
      accessorFn: (row) => row.organization.go_live_at,
      header: 'Go Live',
      cell: ({ row }) => formatGoLive(row.original.organization),
      meta: {
        kind: 'date',
        align: 'end',
        sortable: sortDirections('go_live_at'),
      },
    },
  ]

  if (view === FLORIST_PERFORMANCE_VIEW.ORDERS) {
    return [
      ...columns,
      countColumn('web', 'Web'),
      countColumn('subscription', 'Subscription'),
      countColumn('pos', 'POS'),
      countColumn('condolences', 'Condolences'),
      countColumn('fsn', 'FSN'),
      countColumn('other', 'Other'),
      countColumn('total', 'Total'),
    ]
  }

  if (view === FLORIST_PERFORMANCE_VIEW.MOMENTUM) {
    return [
      ...columns,
      paceColumn('current_7', 'Current 7-Day Avg / Day', momentumMetric),
      paceColumn('prior_7', 'Prior 7-Day Avg / Day', momentumMetric),
      changeColumn(
        'change_7_percent',
        '7-Day Change',
        'change_7',
        'is_new_7',
        'has_no_prior_activity_7'
      ),
      paceColumn('current_30', 'Current 30-Day Avg / Day', momentumMetric),
      paceColumn('prior_30', 'Prior 30-Day Avg / Day', momentumMetric),
      changeColumn(
        'change_30_percent',
        '30-Day Change',
        'change_30',
        'is_new_30',
        'has_no_prior_activity_30'
      ),
    ]
  }

  return [
    ...columns,
    moneyColumn('web', 'Web', view),
    ...(view === FLORIST_PERFORMANCE_VIEW.PLATFORM_REVENUE ? [merchantFeeColumn()] : []),
    moneyColumn('subscription', 'Subscription', view),
    moneyColumn('pos', 'POS', view),
    moneyColumn('other', 'Other', view),
    moneyColumn('total', 'Total', view),
  ]
}

/** The view's money by source, when `metrics` carries that view's block. */
export function getFloristPerformanceAmounts(
  metrics: FloristPerformanceReportSummary | FloristPerformanceReportRowMetrics,
  view: MoneyView
): SourceAmounts | undefined {
  if (view === FLORIST_PERFORMANCE_VIEW.CUSTOMER_SALES) {
    return 'customer_sales' in metrics ? metrics.customer_sales : undefined
  }

  return 'platform_revenue' in metrics ? metrics.platform_revenue : undefined
}

function orderCounts(row: FloristPerformanceReportRow) {
  return 'orders' in row.metrics ? row.metrics.orders : undefined
}

function momentum(row: FloristPerformanceReportRow) {
  return 'momentum' in row.metrics ? row.metrics.momentum : undefined
}

function countColumn(key: keyof FloristPerformanceReportOrderCounts, header: string): Column {
  return {
    id: key,
    accessorFn: (row) => orderCounts(row)?.[key],
    header,
    cell: ({ row }) => formatNumber(orderCounts(row.original)?.[key]),
    meta: {
      kind: 'number',
      align: 'end',
      sortable: sortDirections(key),
    },
  }
}

function moneyColumn(key: keyof SourceAmounts, header: string, view: MoneyView): Column {
  return {
    id: key,
    accessorFn: (row) => getFloristPerformanceAmounts(row.metrics, view)?.[key],
    header,
    cell: ({ row }) =>
      key === 'total' && row.original.platform_earnings ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="inline"
              aria-label={`Platform Revenue breakdown for ${row.original.organization.name}`}
            >
              {formatCurrency(getFloristPerformanceAmounts(row.original.metrics, view)?.[key])}
            </Button>
          </PopoverTrigger>
          <PopoverContent size="wide">
            <PlatformEarningsBreakdown summary={row.original.platform_earnings} />
          </PopoverContent>
        </Popover>
      ) : (
        formatCurrency(getFloristPerformanceAmounts(row.original.metrics, view)?.[key])
      ),
    meta: {
      kind: 'money',
      link: !(view === 'platform_revenue' && key === 'total'),
      align: 'end',
      sortable: sortDirections(key),
    },
  }
}

function paceColumn(key: MomentumWindow, header: string, metric: MomentumMetric): Column {
  return {
    id: key,
    accessorFn: (row) => momentum(row)?.[key].daily_pace,
    header,
    cell: ({ row }) => formatPace(momentum(row.original)?.[key].daily_pace, metric),
    meta: {
      kind: metric === FLORIST_PERFORMANCE_MOMENTUM_METRIC.ORDERS ? 'number' : 'money',
      align: 'end',
      minWidth: '9rem',
      sortable: sortDirections(key),
    },
  }
}

function changeColumn(
  key: 'change_7_percent' | 'change_30_percent',
  header: string,
  sortKey: 'change_7' | 'change_30',
  newKey: 'is_new_7' | 'is_new_30',
  noPriorActivityKey: 'has_no_prior_activity_7' | 'has_no_prior_activity_30'
): Column {
  return {
    id: key,
    accessorFn: (row) => momentum(row)?.[key],
    header,
    cell: ({ row }) => {
      const rowMomentum = momentum(row.original)

      if (rowMomentum?.[newKey]) {
        return <Badge variant="info">New</Badge>
      }

      if (rowMomentum?.[noPriorActivityKey]) {
        return <Badge variant="muted">No prior activity</Badge>
      }

      const change = rowMomentum?.[key]

      if (typeof change !== 'number') {
        return <span className="text-muted-foreground">—</span>
      }

      const prefix = change > 0 ? '+' : ''

      return (
        <span
          className={cn(
            'tabular-nums',
            change > 0 && 'font-medium text-success',
            change < 0 && 'font-medium text-destructive'
          )}
        >
          {`${prefix}${numberWithDecimals.format(change)}%`}
        </span>
      )
    },
    meta: {
      kind: 'number',
      align: 'end',
      link: false,
      sortable: sortDirections(sortKey),
    },
  }
}

function sortDirections(field: FloristPerformanceReportSortField): DirectionalSortConfig {
  return {
    asc: `${field}_asc`,
    desc: `${field}_desc`,
    defaultDirection: 'desc',
  }
}

function formatGoLive(organization: FloristPerformanceReportOrganization) {
  if (!organization.go_live_at) {
    return <span className="text-muted-foreground">Not live</span>
  }

  return dayjs(organization.go_live_at).tz(organization.timezone).format('MMM D, YYYY')
}

/** Orders per day for the orders metric, else a decimal string of money per day. */
function formatPace(value: number | string | null | undefined, metric: MomentumMetric) {
  if (value === null) {
    return <span className="text-muted-foreground">—</span>
  }

  return metric === FLORIST_PERFORMANCE_MOMENTUM_METRIC.ORDERS && typeof value === 'number'
    ? numberWithDecimals.format(value)
    : formatCurrency(value)
}

function merchantFeeColumn(): Column {
  return {
    id: 'merchant_fee_margin',
    accessorFn: (row) => row.platform_earnings?.merchant_fee_margin,
    header: 'Merchant Fees',
    cell: ({ row }) =>
      (row.original.platform_earnings?.has_reportable_merchant_fees ??
      row.original.platform_earnings?.has_v2_activity) ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="inline"
              aria-label={`Merchant Fee breakdown for ${row.original.organization.name}`}
            >
              {formatCurrency(row.original.platform_earnings.merchant_fee_margin)}
            </Button>
          </PopoverTrigger>
          <PopoverContent size="wide">
            <PlatformEarningsBreakdown
              summary={row.original.platform_earnings}
              merchantOnly
              showTotal={false}
            />
          </PopoverContent>
        </Popover>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
    meta: {
      kind: 'money',
      align: 'end',
      link: false,
      sortable: sortDirections('merchant_fee_margin'),
    },
  }
}
